/**
 * EvidenceList
 *
 * Renders a collapsible list of evidence excerpts from the call transcript.
 * Displays speaker labels and timestamps when available.
 */
import { Quote } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '../../ui/badge';
import type { EvidenceItem } from './types';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function speakerLabel(speaker: number | null): string | null {
  if (speaker === null || speaker === undefined) return null;
  return `Speaker ${speaker}`;
}

// ── Props ────────────────────────────────────────────────────────────────────

interface EvidenceListProps {
  evidence: EvidenceItem[];
}

// ── Component ────────────────────────────────────────────────────────────────

export const EvidenceList = ({ evidence }: EvidenceListProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (evidence.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="inline-flex items-center gap-1.5">
          <Quote className="size-3" />
          Evidence from call ({evidence.length})
        </span>
      </button>
      {isOpen && (
        <div className="mt-2 space-y-2">
          {evidence.map((ev, idx) => {
            const speaker = speakerLabel(ev.speaker);
            const timestamp =
              ev.start_time !== null && ev.start_time !== undefined
                ? formatTimestamp(ev.start_time)
                : null;

            return (
              <div
                key={idx}
                className="rounded-md border-l-2 border-muted-foreground/30 bg-muted/50 py-1.5 pl-3 pr-2"
              >
                {(speaker || timestamp) && (
                  <div className="mb-1 flex items-center gap-1.5">
                    {speaker && (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        {speaker}
                      </Badge>
                    )}
                    {timestamp && (
                      <span className="text-[10px] font-medium text-muted-foreground">
                        {timestamp}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-xs leading-relaxed text-muted-foreground italic">
                  &ldquo;{ev.text}&rdquo;
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
